import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  Check, ArrowRight, ArrowLeft, Home, LayoutGrid, Sparkles, Gift, Copy, 
  Calendar as CalendarIcon, Clock, Square, Minus, Plus, MapPin, User, Lock,
  FileText, Download, Mail, BadgePercent, CheckCircle, Eye, EyeOff, Phone
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Tarifs
const TARIF_M2 = 3;
const TARIF_HEURE = 36;
const TARIF_VITRES = {
  fenetre_simple: 6, fenetre_double: 9, porte_fenetre: 12, baie_vitree: 15
};
const TARIF_ENCADREMENTS = {
  fenetre_simple: 3, fenetre_double: 4, porte_fenetre: 5, baie_vitree: 6
};

// Mes Réservations Component
const MesReservations = ({ token }) => {
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchReservations = async () => {
      if (!token) return;
      try {
        const res = await axios.get(`${API}/mes-reservations`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setReservations(res.data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchReservations();
  }, [token]);

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h3 className="font-bold text-[#003264] mb-4">Mes réservations</h3>
        <p className="text-sm text-gray-500">Chargement...</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <h3 className="font-bold text-[#003264] mb-4">Mes réservations</h3>
      {reservations.length === 0 ? (
        <p className="text-sm text-[#8A8D92]">Aucune réservation</p>
      ) : (
        <div className="space-y-3 max-h-[350px] overflow-y-auto">
          {reservations.slice(0, 5).map(resa => (
            <div 
              key={resa.id} 
              className={`p-3 rounded-lg border-l-4 ${
                resa.status === "confirme" 
                  ? "bg-green-50 border-l-green-500" 
                  : resa.status === "annule"
                  ? "bg-red-50 border-l-red-500"
                  : "bg-amber-50 border-l-amber-500"
              }`}
            >
              <div className="flex items-center gap-2 mb-1">
                <CalendarIcon className="w-4 h-4 text-[#003264]" />
                <span className="text-sm font-medium text-[#003264]">
                  {resa.date_souhaitee} à {resa.heure_souhaitee}
                </span>
              </div>
              <p className="text-xs text-[#8A8D92] capitalize">{resa.type_prestation}</p>
              <span className={`text-xs px-2 py-0.5 rounded-full mt-1 inline-block ${
                resa.status === "confirme" 
                  ? "bg-green-100 text-green-700"
                  : resa.status === "annule"
                  ? "bg-red-100 text-red-700"
                  : "bg-amber-100 text-amber-700"
              }`}>
                {resa.status === "en_attente" ? "En attente" : resa.status === "confirme" ? "Confirmée" : resa.status === "annule" ? "Annulée" : resa.status}
              </span>
              {resa.comment_admin && (
                <div className="mt-2 pt-2 border-t border-gray-200">
                  <p className="text-xs text-[#003264] font-medium">Message IKS :</p>
                  <p className="text-xs text-[#4E5257] mt-1">{resa.comment_admin}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Mes Factures Component - Version simplifiée et claire
const MesFactures = ({ token }) => {
  const [factures, setFactures] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFactures = async () => {
      if (!token) return;
      try {
        const res = await axios.get(`${API}/mes-factures`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setFactures(res.data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchFactures();
  }, [token]);

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h3 className="font-bold text-[#003264] mb-4">Mes factures</h3>
        <p className="text-sm text-gray-500">Chargement...</p>
      </div>
    );
  }

  if (factures.length === 0) return null;

  // Format date nicely
  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
  };

  // Get prestation label
  const getPrestationLabel = (type) => {
    const labels = {
      menage: "Ménage",
      vitres: "Nettoyage vitres",
      remise_etat: "Remise en état",
      "Abonnement menage": "Abonnement ménage",
      "Abonnement vitres": "Abonnement vitres"
    };
    return labels[type] || type;
  };

  return (
    <div className="bg-gradient-to-br from-blue-50 to-white rounded-xl border-2 border-blue-100 p-5">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 bg-[#003264] rounded-full flex items-center justify-center">
          <FileText className="w-5 h-5 text-white" />
        </div>
        <div>
          <h3 className="font-bold text-[#003264]">Mes factures</h3>
          <p className="text-xs text-gray-500">{factures.length} document{factures.length > 1 ? "s" : ""}</p>
        </div>
      </div>
      
      <div className="space-y-3">
        {factures.map(facture => (
          <div 
            key={facture.id} 
            className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm"
          >
            {/* Date et type de prestation */}
            <div className="mb-3">
              <p className="text-sm font-semibold text-[#003264]">
                {getPrestationLabel(facture.prestation_type)}
              </p>
              {facture.prestation_date && (
                <p className="text-xs text-gray-500">
                  Intervention du {formatDate(facture.prestation_date)}
                </p>
              )}
              {facture.description && (
                <p className="text-xs text-gray-400 mt-1">{facture.description}</p>
              )}
            </div>
            
            {/* Bouton télécharger bien visible */}
            <a
              href={`${API}/facture/download/${facture.id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 w-full py-2.5 bg-[#003264] text-white rounded-lg hover:bg-[#002244] transition font-medium text-sm"
            >
              <Download className="w-4 h-4" />
              Télécharger la facture
            </a>
          </div>
        ))}
      </div>
    </div>
  );
};

export const EspaceParticulierFlow = () => {
  const navigate = useNavigate();
  const { user, getToken, login, register } = useAuth();
  const [step, setStep] = useState(1);
  const [submitLoading, setSubmitLoading] = useState(false);
  
  const [selectedService, setSelectedService] = useState(null);
  
  // Ménage details
  const [menageMode, setMenageMode] = useState("m2");
  const [menageValeur, setMenageValeur] = useState("");
  
  // Vitres details
  const [vitres, setVitres] = useState({
    fenetre_simple: 0, fenetre_double: 0, porte_fenetre: 0, baie_vitree: 0,
  });
  const [encadrements, setEncadrements] = useState({
    fenetre_simple: 0, fenetre_double: 0, porte_fenetre: 0, baie_vitree: 0,
  });
  
  // Form data
  const [formData, setFormData] = useState({
    adresse: "", code_postal: "", ville: "", date: null, heure: "", code_parrain: "", message: ""
  });
  const [parrainValid, setParrainValid] = useState(null);
  const [parrainPrenom, setParrainPrenom] = useState("");

  // Inline auth for non-logged users
  const [authData, setAuthData] = useState({
    nom: "", prenom: "", email: "", telephone: "", password: ""
  });
  const [showInlineAuth, setShowInlineAuth] = useState(false);
  const [isReturningUser, setIsReturningUser] = useState(false);
  
  // Crédit d'impôt state
  const [wantsCreditImpot, setWantsCreditImpot] = useState(null); // null, true, false
  const [creditImpotData, setCreditImpotData] = useState({
    ci_nom: "", ci_prenom: "", ci_date_naissance: "", ci_email: "",
    ci_telephone: "", ci_numero_fiscal: ""
  });
  const [showPassword, setShowPassword] = useState(false);

  const services = [
    { id: "menage", icon: Home, label: "Ménage", desc: "Nettoyage de votre domicile", color: "bg-blue-50 border-blue-200" },
    { id: "vitres", icon: LayoutGrid, label: "Vitres", desc: "Nettoyage de vos vitres", color: "bg-emerald-50 border-emerald-200" },
    { id: "remise_etat", icon: Sparkles, label: "Remise en état", desc: "Après travaux ou déménagement", color: "bg-amber-50 border-amber-200" },
  ];

  const vitresItems = [
    { key: "fenetre_simple", label: "Fenêtre simple", prix: 6 },
    { key: "fenetre_double", label: "Fenêtre double", prix: 9 },
    { key: "porte_fenetre", label: "Porte-fenêtre", prix: 12 },
    { key: "baie_vitree", label: "Baie vitrée", prix: 15 },
  ];

  const encadrementsItems = [
    { key: "fenetre_simple", label: "Encadrement simple", prix: 3 },
    { key: "fenetre_double", label: "Encadrement double", prix: 4 },
    { key: "porte_fenetre", label: "Encadrement porte-fenêtre", prix: 5 },
    { key: "baie_vitree", label: "Encadrement baie vitrée", prix: 6 },
  ];

  const heures = ["08:00", "09:00", "10:00", "11:00", "14:00", "15:00", "16:00", "17:00"];

  // Vérifier si le code postal est dans le département 34 (Hérault)
  const isCodePostalHerault = (cp) => {
    return cp && cp.length >= 2 && cp.startsWith("34");
  };

  const codePostalHorsZone = formData.code_postal && formData.code_postal.length >= 2 && !isCodePostalHerault(formData.code_postal);

  // Load pending reservation from localStorage
  useEffect(() => {
    const pending = localStorage.getItem('pendingReservation');
    if (pending) {
      const data = JSON.parse(pending);
      if (data.mode === 'ponctuel' && data.reservationData) {
        const r = data.reservationData;
        setSelectedService(r.selectedService);
        setMenageMode(r.menageMode || "m2");
        setMenageValeur(r.menageValeur || "");
        setVitres(r.vitres || { fenetre_simple: 0, fenetre_double: 0, porte_fenetre: 0, baie_vitree: 0 });
        setEncadrements(r.encadrements || { fenetre_simple: 0, fenetre_double: 0, porte_fenetre: 0, baie_vitree: 0 });
        setFormData(r.formData || { adresse: "", code_postal: "", ville: "", date: null, heure: "", code_parrain: "", message: "" });
        if (r.formData?.date) {
          setFormData(prev => ({ ...prev, date: new Date(r.formData.date) }));
        }
        setStep(r.step || 1);
        
        // If user just logged in, auto-submit
        if (user && data.autoSubmit) {
          setTimeout(() => {
            submitReservation();
          }, 500);
        }
      }
    }
  }, [user]);

  // Pre-fill address from user profile if logged in
  useEffect(() => {
    if (user && !formData.adresse) {
      setFormData(prev => ({
        ...prev,
        adresse: user.adresse || prev.adresse,
        code_postal: user.code_postal || prev.code_postal,
        ville: user.ville || prev.ville
      }));
    }
  }, [user]);

  // Validate parrain code
  useEffect(() => {
    const validateCode = async () => {
      if (!formData.code_parrain || formData.code_parrain.length < 4) {
        setParrainValid(null);
        setParrainPrenom("");
        return;
      }
      if (user && formData.code_parrain === user.code_parrainage) {
        setParrainValid(false);
        setParrainPrenom("");
        return;
      }
      try {
        const res = await axios.get(`${API}/parrainage/validate/${formData.code_parrain}`);
        setParrainValid(res.data.valid);
        setParrainPrenom(res.data.parrain_prenom || "");
      } catch (e) {
        setParrainValid(false);
      }
    };
    const t = setTimeout(validateCode, 500);
    return () => clearTimeout(t);
  }, [formData.code_parrain, user]);

  // Calculate price
  const calculatePrice = () => {
    if (selectedService === "menage") {
      const val = parseFloat(menageValeur) || 0;
      if (val <= 0) return { total: 0, apresCredit: 0 };
      const total = menageMode === "m2" ? val * TARIF_M2 : val * TARIF_HEURE;
      return { total, apresCredit: total * 0.5 };
    }
    
    if (selectedService === "vitres") {
      let total = 0;
      Object.entries(vitres).forEach(([key, qty]) => {
        total += qty * TARIF_VITRES[key];
      });
      Object.entries(encadrements).forEach(([key, qty]) => {
        total += qty * TARIF_ENCADREMENTS[key];
      });
      return { total, apresCredit: total * 0.5 };
    }
    
    return { total: 0, apresCredit: 0 };
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const updateVitre = (key, delta) => {
    setVitres(prev => ({ ...prev, [key]: Math.max(0, prev[key] + delta) }));
  };

  const updateEncadrement = (key, delta) => {
    setEncadrements(prev => ({ ...prev, [key]: Math.max(0, prev[key] + delta) }));
  };

  const canProceedStep2 = () => {
    if (!selectedService) return false;
    if (selectedService === "menage") {
      return parseFloat(menageValeur) > 0;
    }
    if (selectedService === "vitres") {
      const hasVitres = Object.values(vitres).some(v => v > 0);
      const hasEncadrements = Object.values(encadrements).some(v => v > 0);
      return hasVitres || hasEncadrements;
    }
    return selectedService === "remise_etat";
  };

  const submitReservation = async () => {
    if (!formData.date || !formData.heure || !formData.adresse) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }
    
    setSubmitLoading(true);
    try {
      const price = calculatePrice();
      const details = {
        service: selectedService,
        ...(selectedService === "menage" && { mode: menageMode, valeur: parseFloat(menageValeur) }),
        ...(selectedService === "vitres" && { vitres, encadrements }),
        prix_total: price.total,
        prix_apres_credit: price.apresCredit
      };

      const res = await axios.post(`${API}/reservation`, {
        adresse: `${formData.adresse}, ${formData.code_postal} ${formData.ville}`,
        type_prestation: selectedService,
        date_souhaitee: format(formData.date, "yyyy-MM-dd"),
        heure_souhaitee: formData.heure,
        details_prestation: details,
        code_parrain: parrainValid ? formData.code_parrain : null,
        message: formData.message,
        avance_credit_impot: wantsCreditImpot || false,
        credit_impot_data: wantsCreditImpot ? creditImpotData : null
      }, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      
      // Clear pending reservation
      localStorage.removeItem('pendingReservation');
      
      let successMsg = "Réservation confirmée ! Vous recevrez un email de confirmation.";
      if (res.data.parrain_bonus_applied) {
        successMsg = `Réservation confirmée ! ${parrainPrenom} a reçu 15€ de crédit grâce à vous.`;
      }
      
      toast.success(successMsg);
      
      // Navigate back to choice page
      navigate("/particuliers");
    } catch (error) {
      toast.error("Erreur lors de la réservation");
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.date || !formData.heure || !formData.adresse) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }
    
    if (!user) {
      // Show inline registration form instead of redirecting
      setShowInlineAuth(true);
      // Scroll to the auth form
      setTimeout(() => {
        document.getElementById('inline-auth-section')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
      return;
    }
    
    // User is logged in - submit directly
    await submitReservation();
  };

  const handleInlineRegisterAndSubmit = async () => {
    // Validate auth fields
    if (!isReturningUser) {
      if (!authData.nom || !authData.prenom || !authData.email || !authData.telephone || !authData.password) {
        toast.error("Veuillez remplir tous les champs");
        return;
      }
      if (authData.password.length < 6) {
        toast.error("Le mot de passe doit contenir au moins 6 caractères");
        return;
      }
    } else {
      if (!authData.email || !authData.password) {
        toast.error("Veuillez remplir votre email et mot de passe");
        return;
      }
    }

    setSubmitLoading(true);
    try {
      if (isReturningUser) {
        // Login existing user
        await login(authData.email, authData.password);
      } else {
        // Register new user with the address from the booking form
        await register({
          nom: authData.nom,
          prenom: authData.prenom,
          email: authData.email,
          telephone: authData.telephone,
          password: authData.password,
          adresse: formData.adresse,
          code_postal: formData.code_postal,
          ville: formData.ville
        });
      }
      
      toast.success(isReturningUser ? "Connexion réussie !" : "Compte créé ! Vérifiez votre email.");
      
      // Now submit the reservation with the new token
      setTimeout(async () => {
        try {
          const token = localStorage.getItem("iks_token");
          const price = calculatePrice();
          const details = {
            service: selectedService,
            ...(selectedService === "menage" && { mode: menageMode, valeur: parseFloat(menageValeur) }),
            ...(selectedService === "vitres" && { vitres, encadrements }),
            prix_total: price.total,
            prix_apres_credit: price.apresCredit
          };

          await axios.post(`${API}/reservation`, {
            adresse: `${formData.adresse}, ${formData.code_postal} ${formData.ville}`,
            type_prestation: selectedService,
            date_souhaitee: format(formData.date, "yyyy-MM-dd"),
            heure_souhaitee: formData.heure,
            details_prestation: details,
            code_parrain: parrainValid ? formData.code_parrain : null,
            message: formData.message,
            avance_credit_impot: wantsCreditImpot || false,
            credit_impot_data: wantsCreditImpot ? creditImpotData : null
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          localStorage.removeItem('pendingReservation');
          if (!isReturningUser) {
            toast.success("Réservation enregistrée ! Vérifiez votre email pour activer votre compte.");
          } else {
            toast.success("Réservation confirmée !");
          }
          navigate("/particuliers");
        } catch (error) {
          const detail = error.response?.data?.detail;
          const msg = typeof detail === 'string' ? detail : "Erreur lors de la réservation";
          toast.error(msg);
        } finally {
          setSubmitLoading(false);
        }
      }, 500);
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : "Erreur lors de la création du compte";
      toast.error(msg);
      setSubmitLoading(false);
    }
  };

  const price = calculatePrice();

  const CounterRow = ({ item, value, onUpdate }) => (
    <div className="flex items-center justify-between py-2">
      <div>
        <p className="text-sm font-medium text-[#003264]">{item.label}</p>
        <p className="text-xs text-[#8A8D92]">{item.prix}€</p>
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={() => onUpdate(item.key, -1)}
          className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center"
          type="button"
        >
          <Minus className="w-3 h-3 text-[#4E5257]" />
        </button>
        <span className="w-8 text-center text-sm font-semibold text-[#003264]">{value}</span>
        <button
          onClick={() => onUpdate(item.key, 1)}
          className="w-8 h-8 rounded-full bg-[#003264] hover:bg-[#002244] flex items-center justify-center text-white"
          type="button"
        >
          <Plus className="w-3 h-3" />
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50" data-testid="espace-particulier-flow">
      <div className="max-w-5xl mx-auto px-6 py-12">
        
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-[#003264] mb-1">
              Prestation ponctuelle
            </h1>
            <p className="text-[#8A8D92]">Configurez votre prestation en quelques clics</p>
          </div>
          {user && (
            <div className="flex items-center gap-2 text-sm text-[#8A8D92]">
              <User className="w-4 h-4" />
              {user.email}
            </div>
          )}
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Form */}
          <div className="lg:col-span-2 space-y-6">
            
            {/* Step 1: Choose Service */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="bg-[#003264] text-white p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="w-7 h-7 rounded-full bg-white/20 flex items-center justify-center text-sm font-bold">1</span>
                  <span className="font-semibold">Type de prestation</span>
                </div>
                {selectedService && step > 1 && (
                  <button onClick={() => setStep(1)} className="text-sm text-white/70 hover:text-white">
                    Modifier
                  </button>
                )}
              </div>
              
              {step === 1 && (
                <div className="p-6">
                  <div className="grid gap-3 mb-6">
                    {services.map(service => {
                      const isSelected = selectedService === service.id;
                      return (
                        <button
                          key={service.id}
                          onClick={() => setSelectedService(service.id)}
                          className={`relative p-4 rounded-xl border-2 text-left transition-all flex items-center gap-4 ${
                            isSelected 
                              ? "border-[#003264] bg-blue-50" 
                              : "border-gray-200 hover:border-gray-300"
                          }`}
                          data-testid={`service-${service.id}`}
                        >
                          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                            isSelected ? "bg-[#003264] text-white" : "bg-gray-100 text-[#4E5257]"
                          }`}>
                            <service.icon className="w-6 h-6" />
                          </div>
                          <div className="flex-1">
                            <p className="font-semibold text-[#003264]">{service.label}</p>
                            <p className="text-sm text-[#8A8D92]">{service.desc}</p>
                          </div>
                          {isSelected && (
                            <div className="w-6 h-6 rounded-full bg-[#003264] flex items-center justify-center">
                              <Check className="w-4 h-4 text-white" />
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>

                  {/* Service Details */}
                  {selectedService === "menage" && (
                    <div className="bg-gray-50 rounded-xl p-4 mb-6">
                      <p className="text-sm font-semibold text-[#003264] mb-3">Configurez votre ménage</p>
                      <div className="flex gap-2 mb-4">
                        <button
                          onClick={() => setMenageMode("m2")}
                          className={`flex-1 py-3 px-3 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
                            menageMode === "m2" ? "bg-[#003264] text-white" : "bg-white border text-[#4E5257]"
                          }`}
                          type="button"
                        >
                          <Square className="w-4 h-4" /> Au m²
                        </button>
                        <button
                          onClick={() => setMenageMode("heures")}
                          className={`flex-1 py-3 px-3 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
                            menageMode === "heures" ? "bg-[#003264] text-white" : "bg-white border text-[#4E5257]"
                          }`}
                          type="button"
                        >
                          <Clock className="w-4 h-4" /> À l'heure
                        </button>
                      </div>
                      <Input
                        type="number"
                        min="1"
                        placeholder={menageMode === "m2" ? "Surface en m²" : "Nombre d'heures"}
                        value={menageValeur}
                        onChange={(e) => setMenageValeur(e.target.value)}
                        className="h-12 text-lg bg-white"
                        data-testid="input-menage-valeur"
                      />
                      <p className="text-xs text-emerald-600 mt-2 font-medium">
                        {menageMode === "m2" ? "3€/m² → 1,50€/m² avec avance immédiate" : "36€/h → 18€/h avec avance immédiate"}
                      </p>
                    </div>
                  )}

                  {selectedService === "vitres" && (
                    <div className="bg-gray-50 rounded-xl p-4 mb-6 max-h-[350px] overflow-y-auto">
                      <p className="text-sm font-semibold text-[#003264] mb-3">Configurez vos vitres</p>
                      <div className="space-y-1">
                        <p className="text-xs font-medium text-[#8A8D92] uppercase">Vitres</p>
                        {vitresItems.map(item => (
                          <CounterRow key={item.key} item={item} value={vitres[item.key]} onUpdate={updateVitre} />
                        ))}
                      </div>
                      <div className="border-t mt-4 pt-4 space-y-1">
                        <p className="text-xs font-medium text-[#8A8D92] uppercase">Encadrements</p>
                        {encadrementsItems.map(item => (
                          <CounterRow key={item.key} item={item} value={encadrements[item.key]} onUpdate={updateEncadrement} />
                        ))}
                      </div>
                    </div>
                  )}

                  {selectedService === "remise_etat" && (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
                      <p className="text-sm font-semibold text-[#003264] mb-2">Remise en état</p>
                      <p className="text-sm text-[#4E5257]">
                        Pour les remises en état (après travaux, déménagement, etc.), nous établirons un devis personnalisé après votre demande.
                      </p>
                    </div>
                  )}

                  <Button
                    onClick={() => setStep(2)}
                    disabled={!canProceedStep2()}
                    className="w-full bg-[#003264] hover:bg-[#002244] text-white"
                    data-testid="btn-step1-next"
                  >
                    Continuer <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              )}

              {step > 1 && (
                <div className="p-4 bg-gray-50 border-t">
                  <div className="flex items-center gap-3">
                    {selectedService && (
                      <>
                        {selectedService === "menage" && <Home className="w-5 h-5 text-[#003264]" />}
                        {selectedService === "vitres" && <LayoutGrid className="w-5 h-5 text-[#003264]" />}
                        {selectedService === "remise_etat" && <Sparkles className="w-5 h-5 text-[#003264]" />}
                        <span className="font-medium text-[#003264]">
                          {services.find(s => s.id === selectedService)?.label}
                        </span>
                        {selectedService === "menage" && menageValeur && (
                          <span className="text-[#8A8D92]">
                            — {menageValeur} {menageMode === "m2" ? "m²" : "h"}
                          </span>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Step 2: Date & Address */}
            {step >= 2 && (
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="bg-[#003264] text-white p-4 flex items-center gap-3">
                  <span className="w-7 h-7 rounded-full bg-white/20 flex items-center justify-center text-sm font-bold">2</span>
                  <span className="font-semibold">Date et adresse</span>
                </div>
                
                <div className="p-6 space-y-5">
                  {/* Address */}
                  <div>
                    <Label className="text-[#003264] font-semibold flex items-center gap-2">
                      <MapPin className="w-4 h-4" /> Adresse d'intervention *
                    </Label>
                    <Input 
                      name="adresse" 
                      value={formData.adresse} 
                      onChange={handleChange} 
                      className="mt-2" 
                      placeholder="Rue"
                      data-testid="input-adresse"
                    />
                    <div className="grid grid-cols-2 gap-3 mt-2">
                      <Input 
                        name="code_postal" 
                        value={formData.code_postal} 
                        onChange={handleChange} 
                        placeholder="Code postal"
                        maxLength={5}
                      />
                      <Input 
                        name="ville" 
                        value={formData.ville} 
                        onChange={handleChange} 
                        placeholder="Ville"
                      />
                    </div>
                  </div>

                  {/* Date */}
                  <div>
                    <Label className="text-[#003264] font-semibold flex items-center gap-2">
                      <CalendarIcon className="w-4 h-4" /> Date souhaitée *
                    </Label>
                    <div className="mt-2 flex justify-center">
                      <Calendar
                        mode="single"
                        selected={formData.date}
                        onSelect={(date) => setFormData(prev => ({ ...prev, date }))}
                        disabled={(date) => {
                          const minDate = new Date();
                          minDate.setDate(minDate.getDate() + 2);
                          minDate.setHours(0, 0, 0, 0);
                          return date < minDate;
                        }}
                        className="rounded-lg border"
                        locale={fr}
                      />
                    </div>
                  </div>

                  {/* Time */}
                  <div>
                    <Label className="text-[#003264] font-semibold flex items-center gap-2">
                      <Clock className="w-4 h-4" /> Heure souhaitée *
                    </Label>
                    <Select value={formData.heure} onValueChange={(v) => setFormData(prev => ({ ...prev, heure: v }))}>
                      <SelectTrigger className="mt-2" data-testid="select-heure">
                        <SelectValue placeholder="Choisir une heure" />
                      </SelectTrigger>
                      <SelectContent>
                        {heures.map(h => (
                          <SelectItem key={h} value={h}>{h}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Parrainage */}
                  <div>
                    <Label className="text-[#003264]">Code parrainage (optionnel)</Label>
                    <Input 
                      name="code_parrain" 
                      value={formData.code_parrain} 
                      onChange={handleChange} 
                      className="mt-1" 
                      placeholder="Ex: IKS1A2B3C"
                      data-testid="input-parrain"
                    />
                    {parrainValid === true && (
                      <p className="text-xs text-emerald-600 mt-1">
                        ✓ Code valide ! {parrainPrenom} recevra 15€ de crédit.
                      </p>
                    )}
                    {parrainValid === false && formData.code_parrain && (
                      <p className="text-xs text-red-500 mt-1">Code invalide</p>
                    )}
                  </div>

                  {/* Message */}
                  <div>
                    <Label className="text-[#003264]">Message (optionnel)</Label>
                    <Textarea 
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Précisions, accès, digicode..."
                      rows={3}
                      className="mt-1"
                      data-testid="textarea-message"
                    />
                  </div>

                  {/* Avance du crédit d'impôt */}
                  <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5" data-testid="credit-impot-section">
                    <h4 className="font-bold text-[#003264] mb-1 flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      Avance immédiate du crédit d'impôt
                    </h4>
                    <p className="text-sm text-gray-600 mb-4">
                      Vous pouvez bénéficier de l'avance immédiate du crédit d'impôt et ne payer que <strong>50 % du montant</strong> de la prestation.
                    </p>
                    <div className="flex gap-3 mb-4">
                      <button
                        type="button"
                        onClick={() => setWantsCreditImpot(true)}
                        className={`flex-1 py-3 rounded-lg font-semibold text-sm border-2 transition-all ${
                          wantsCreditImpot === true
                            ? "bg-emerald-600 text-white border-emerald-600"
                            : "bg-white text-gray-700 border-gray-200 hover:border-emerald-300"
                        }`}
                        data-testid="credit-impot-oui"
                      >
                        Oui, j'en bénéficie
                      </button>
                      <button
                        type="button"
                        onClick={() => setWantsCreditImpot(false)}
                        className={`flex-1 py-3 rounded-lg font-semibold text-sm border-2 transition-all ${
                          wantsCreditImpot === false
                            ? "bg-gray-600 text-white border-gray-600"
                            : "bg-white text-gray-700 border-gray-200 hover:border-gray-300"
                        }`}
                        data-testid="credit-impot-non"
                      >
                        Non merci
                      </button>
                    </div>

                    {wantsCreditImpot === true && (
                      <div className="space-y-3 pt-3 border-t border-emerald-200" data-testid="credit-impot-form">
                        <p className="text-xs text-emerald-700 font-medium">
                          Merci de renseigner les informations nécessaires pour l'avance du crédit d'impôt :
                        </p>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-sm text-gray-700">Nom *</Label>
                            <Input
                              value={creditImpotData.ci_nom}
                              onChange={e => setCreditImpotData(p => ({...p, ci_nom: e.target.value}))}
                              placeholder="Dupont"
                              className="mt-1 h-11"
                              data-testid="ci-nom"
                            />
                          </div>
                          <div>
                            <Label className="text-sm text-gray-700">Prénom *</Label>
                            <Input
                              value={creditImpotData.ci_prenom}
                              onChange={e => setCreditImpotData(p => ({...p, ci_prenom: e.target.value}))}
                              placeholder="Marie"
                              className="mt-1 h-11"
                              data-testid="ci-prenom"
                            />
                          </div>
                        </div>
                        <div>
                          <Label className="text-sm text-gray-700">Date de naissance *</Label>
                          <Input
                            type="date"
                            value={creditImpotData.ci_date_naissance}
                            onChange={e => setCreditImpotData(p => ({...p, ci_date_naissance: e.target.value}))}
                            className="mt-1 h-11"
                            data-testid="ci-date-naissance"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-sm text-gray-700">E-mail *</Label>
                            <Input
                              type="email"
                              value={creditImpotData.ci_email}
                              onChange={e => setCreditImpotData(p => ({...p, ci_email: e.target.value}))}
                              placeholder="marie@email.com"
                              className="mt-1 h-11"
                              data-testid="ci-email"
                            />
                          </div>
                          <div>
                            <Label className="text-sm text-gray-700">Téléphone *</Label>
                            <Input
                              value={creditImpotData.ci_telephone}
                              onChange={e => setCreditImpotData(p => ({...p, ci_telephone: e.target.value}))}
                              placeholder="06 12 34 56 78"
                              className="mt-1 h-11"
                              data-testid="ci-telephone"
                            />
                          </div>
                        </div>
                        <div>
                          <Label className="text-sm text-gray-700">Numéro fiscal *</Label>
                          <Input
                            value={creditImpotData.ci_numero_fiscal}
                            onChange={e => setCreditImpotData(p => ({...p, ci_numero_fiscal: e.target.value}))}
                            placeholder="Votre numéro fiscal à 13 chiffres"
                            className="mt-1 h-11"
                            data-testid="ci-numero-fiscal"
                          />
                          <p className="text-xs text-gray-400 mt-1">
                            Disponible sur votre avis d'imposition ou sur impots.gouv.fr
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Si hors zone 34 - Message contact */}
                  {codePostalHorsZone ? (
                    <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-5 text-center">
                      <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <MapPin className="w-6 h-6 text-amber-600" />
                      </div>
                      <h4 className="font-bold text-[#003264] mb-2">Zone non couverte</h4>
                      <p className="text-sm text-gray-600 mb-4">
                        Votre adresse se situe hors de notre zone d'intervention habituelle (Hérault - 34).
                        <br />Pour cette prestation, un <strong>devis personnalisé</strong> est nécessaire.
                      </p>
                      <a
                        href="mailto:contact@iks-services.fr?subject=Demande de devis hors zone 34&body=Bonjour,%0A%0AJe souhaite obtenir un devis pour une prestation à l'adresse suivante :%0A%0ACode postal : "
                        className="inline-flex items-center justify-center gap-2 w-full py-3 bg-[#003264] text-white rounded-lg hover:bg-[#002244] transition font-medium"
                      >
                        <Mail className="w-4 h-4" />
                        Demander un devis par email
                      </a>
                      <p className="text-xs text-gray-500 mt-3">
                        Ou appelez-nous au <strong>06 25 16 80 27</strong>
                      </p>
                    </div>
                  ) : (
                    <>
                      {/* Inline Auth Form for non-logged users */}
                      {!user && showInlineAuth && (
                        <div id="inline-auth-section" className="bg-white border-2 border-[#003264] rounded-xl p-5 space-y-4" data-testid="inline-auth-form">
                          <div className="flex items-center gap-3 mb-2">
                            <div className="w-10 h-10 bg-[#003264] rounded-full flex items-center justify-center">
                              <User className="w-5 h-5 text-white" />
                            </div>
                            <div>
                              <h3 className="font-bold text-[#003264]">
                                {isReturningUser ? "Connexion rapide" : "Dernière étape — vos coordonnées"}
                              </h3>
                              <p className="text-xs text-gray-500">
                                {isReturningUser ? "Connectez-vous pour finaliser" : "Votre compte est créé automatiquement"}
                              </p>
                            </div>
                          </div>

                          {!isReturningUser && (
                            <>
                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <Label className="text-gray-700 text-sm">Prénom *</Label>
                                  <Input 
                                    value={authData.prenom} 
                                    onChange={(e) => setAuthData(p => ({...p, prenom: e.target.value}))} 
                                    placeholder="Jean" 
                                    className="mt-1 h-11"
                                    data-testid="inline-prenom"
                                  />
                                </div>
                                <div>
                                  <Label className="text-gray-700 text-sm">Nom *</Label>
                                  <Input 
                                    value={authData.nom} 
                                    onChange={(e) => setAuthData(p => ({...p, nom: e.target.value}))} 
                                    placeholder="Dupont" 
                                    className="mt-1 h-11"
                                    data-testid="inline-nom"
                                  />
                                </div>
                              </div>
                              <div>
                                <Label className="text-gray-700 text-sm">Téléphone *</Label>
                                <div className="relative mt-1">
                                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                  <Input 
                                    value={authData.telephone} 
                                    onChange={(e) => setAuthData(p => ({...p, telephone: e.target.value}))} 
                                    placeholder="06 12 34 56 78" 
                                    className="pl-10 h-11"
                                    data-testid="inline-telephone"
                                  />
                                </div>
                              </div>
                            </>
                          )}
                          
                          <div>
                            <Label className="text-gray-700 text-sm">Email *</Label>
                            <div className="relative mt-1">
                              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                              <Input 
                                type="email"
                                value={authData.email} 
                                onChange={(e) => setAuthData(p => ({...p, email: e.target.value}))} 
                                placeholder="jean.dupont@email.com" 
                                className="pl-10 h-11"
                                data-testid="inline-email"
                              />
                            </div>
                          </div>
                          
                          <div>
                            <Label className="text-gray-700 text-sm">Mot de passe *</Label>
                            <div className="relative mt-1">
                              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                              <Input 
                                type={showPassword ? "text" : "password"}
                                value={authData.password} 
                                onChange={(e) => setAuthData(p => ({...p, password: e.target.value}))} 
                                placeholder="Min. 6 caractères"
                                minLength={6}
                                className="pl-10 pr-10 h-11"
                                data-testid="inline-password"
                              />
                              <button type="button" onClick={() => setShowPassword(!showPassword)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                              </button>
                            </div>
                          </div>

                          {/* Address reminder */}
                          <div className="bg-gray-50 rounded-lg p-3 flex items-center gap-2 text-sm text-gray-600">
                            <MapPin className="w-4 h-4 text-gray-400 flex-shrink-0" />
                            <span>{formData.adresse}, {formData.code_postal} {formData.ville}</span>
                            <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0 ml-auto" />
                          </div>
                          
                          <Button
                            onClick={handleInlineRegisterAndSubmit}
                            disabled={submitLoading}
                            className="w-full bg-[#003264] hover:bg-[#002244] text-white py-6 text-base"
                            data-testid="btn-inline-submit"
                          >
                            {submitLoading ? "Création du compte et réservation..." : (
                              <>
                                {isReturningUser ? "Se connecter et réserver" : "Créer mon compte et réserver"}
                                <ArrowRight className="w-5 h-5 ml-2" />
                              </>
                            )}
                          </Button>

                          <button
                            onClick={() => setIsReturningUser(!isReturningUser)}
                            className="w-full text-center text-sm text-gray-500 hover:text-[#003264] transition"
                            data-testid="toggle-auth-mode"
                          >
                            {isReturningUser 
                              ? <>Pas encore de compte ? <span className="font-semibold text-[#003264]">Créer un compte</span></> 
                              : <>Déjà un compte ? <span className="font-semibold text-[#003264]">Se connecter</span></>
                            }
                          </button>
                        </div>
                      )}

                      {/* Submit button (when auth form not shown OR user logged in) */}
                      {(user || !showInlineAuth) && (
                        <>
                          <Button
                            onClick={handleSubmit}
                            disabled={submitLoading || !formData.adresse || !formData.heure || !formData.date || !formData.code_postal}
                            className="w-full bg-[#003264] hover:bg-[#002244] text-white py-6 text-base"
                            data-testid="btn-submit"
                          >
                            {submitLoading ? "Envoi en cours..." : (
                              <>
                                {user ? "Confirmer la réservation" : "Réserver maintenant"}
                                <ArrowRight className="w-5 h-5 ml-2" />
                              </>
                            )}
                          </Button>
                          
                          {!user && (
                            <p className="text-xs text-center text-gray-500">
                              Vous créerez votre compte à l'étape suivante — c'est rapide !
                            </p>
                          )}
                        </>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Right Sidebar */}
          <div className="space-y-6">
            
            {/* Price Summary */}
            {selectedService && selectedService !== "remise_etat" && price.total > 0 && (
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid="price-summary">
                <div className="p-6">
                  <h3 className="font-bold text-[#003264] mb-4">Récapitulatif</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-[#8A8D92]">Prix TTC</span>
                      <span className="font-bold text-gray-400 line-through">{price.total.toFixed(0)}€</span>
                    </div>
                  </div>
                </div>
                <div className="bg-emerald-50 border-t-2 border-emerald-200 p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <BadgePercent className="w-4 h-4 text-emerald-600" />
                    <span className="text-xs font-bold text-emerald-700 uppercase">Avance immédiate</span>
                  </div>
                  <div className="flex items-baseline justify-between">
                    <span className="text-sm text-emerald-700">Vous payez</span>
                    <span className="font-black text-2xl text-emerald-600">{price.apresCredit.toFixed(0)}€</span>
                  </div>
                  <p className="text-xs text-emerald-600 mt-1">
                    L'État verse directement les {price.apresCredit.toFixed(0)}€ restants
                  </p>
                </div>
              </div>
            )}

            {/* Parrainage Card - only if logged in */}
            {user && (
              <div className="bg-gradient-to-br from-amber-50 to-amber-100 border border-amber-200 rounded-xl p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-amber-200 flex items-center justify-center">
                    <Gift className="w-5 h-5 text-amber-700" />
                  </div>
                  <div>
                    <h3 className="font-bold text-[#003264] text-sm">Parrainez vos proches</h3>
                    <p className="text-xs text-[#4E5257]">Gagnez <strong className="text-amber-600">15€</strong> par filleul</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 bg-white rounded-lg p-2">
                  <span className="font-mono font-bold text-[#003264] flex-1 text-center">
                    {user.code_parrainage}
                  </span>
                  <button 
                    onClick={() => {
                      navigator.clipboard.writeText(user.code_parrainage);
                      toast.success("Code copié !");
                    }} 
                    className="p-2 hover:bg-gray-100 rounded"
                  >
                    <Copy className="w-4 h-4 text-[#4E5257]" />
                  </button>
                </div>
              </div>
            )}

            {/* Mes réservations - only if logged in */}
            {user && <MesReservations token={getToken()} />}

            {/* Mes factures - only if logged in */}
            {user && <MesFactures token={getToken()} />}

            {/* Info for non-logged users */}
            {!user && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                    <BadgePercent className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-[#003264] text-sm">Avance immédiate</h3>
                    <p className="text-xs text-emerald-600">Ne payez que 50% !</p>
                  </div>
                </div>
                <ul className="text-xs text-gray-600 space-y-1">
                  <li className="flex items-center gap-1"><CheckCircle className="w-3 h-3 text-emerald-500" /> Crédit d'impôt déduit automatiquement</li>
                  <li className="flex items-center gap-1"><CheckCircle className="w-3 h-3 text-emerald-500" /> Aucune avance de trésorerie</li>
                  <li className="flex items-center gap-1"><CheckCircle className="w-3 h-3 text-emerald-500" /> Compte gratuit en 30 secondes</li>
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
